import { useEffect, useState, useRef } from "react";
import { api, resolveImg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Upload, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Media() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [edit, setEdit] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media", { params: { limit: 100 } }).then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} Bild(er) hochgeladen.`); load();
    } catch { toast.error("Upload fehlgeschlagen."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Bild aus der Mediathek entfernen?")) return;
    await api.delete(`/admin/media/${id}`); toast.success("Entfernt."); load();
  };

  const saveMeta = async () => {
    await api.put(`/admin/media/${edit.id}`, { caption: edit.caption, credit: edit.credit, alt: edit.alt });
    toast.success("Gespeichert."); setEdit(null); load();
  };

  const copyUrl = (url) => { navigator.clipboard.writeText(resolveImg(url)); toast.success("URL kopiert."); };

  return (
    <AdminLayout title="Mediathek">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-muted-foreground font-body">{items.length} Bilder in der Bibliothek. Mehrfach-Upload möglich.</p>
        <button data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 bg-gold text-prussian-dark font-body font-bold text-sm uppercase tracking-wide px-5 py-2.5 hover:bg-gold-hover disabled:opacity-50">
          <Upload size={16} /> {uploading ? "Lädt…" : "Bilder hochladen"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
      </div>

      <div data-testid="admin-media-library" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {items.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="bg-white border border-edborder group">
            <div className="aspect-square overflow-hidden bg-offwhite">
              <img src={resolveImg(m.url)} alt={m.alt || m.original_filename} className="w-full h-full object-cover" />
            </div>
            <div className="p-2">
              <p className="text-[11px] text-muted-foreground truncate font-body" title={m.original_filename}>{m.original_filename}</p>
              {m.caption && <p className="text-[11px] text-charcoal truncate">{m.caption}</p>}
              <div className="flex gap-1 mt-2">
                <button onClick={() => setEdit(m)} className="flex-1 text-[11px] font-body border border-edborder py-1 hover:bg-offwhite">Bearbeiten</button>
                <button onClick={() => copyUrl(m.url)} title="URL kopieren" className="w-7 border border-edborder flex items-center justify-center hover:bg-offwhite"><Copy size={12} /></button>
                <button onClick={() => remove(m.id)} title="Löschen" className="w-7 border border-edborder text-breaking flex items-center justify-center hover:bg-breaking/10"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full text-center text-muted-foreground py-16 font-body">Noch keine Bilder hochgeladen.</p>}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEdit(null)}>
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-headline font-bold text-xl text-prussian mb-4">Bild bearbeiten</h2>
            <img src={resolveImg(edit.url)} alt="" className="w-full aspect-video object-cover border border-edborder mb-4" />
            <label className="block text-xs uppercase tracking-wide text-muted-foreground font-body mb-1">Legende</label>
            <input value={edit.caption || ""} onChange={(e) => setEdit({ ...edit, caption: e.target.value })} className="w-full border border-edborder px-3 py-2 text-sm mb-3 outline-none focus:border-prussian" />
            <label className="block text-xs uppercase tracking-wide text-muted-foreground font-body mb-1">Bildnachweis</label>
            <input value={edit.credit || ""} onChange={(e) => setEdit({ ...edit, credit: e.target.value })} className="w-full border border-edborder px-3 py-2 text-sm mb-3 outline-none focus:border-prussian" />
            <label className="block text-xs uppercase tracking-wide text-muted-foreground font-body mb-1">Alt-Text</label>
            <input value={edit.alt || ""} onChange={(e) => setEdit({ ...edit, alt: e.target.value })} className="w-full border border-edborder px-3 py-2 text-sm mb-5 outline-none focus:border-prussian" />
            <div className="flex gap-2">
              <button onClick={saveMeta} className="flex-1 bg-prussian text-white font-body font-semibold text-sm py-2.5 hover:bg-prussian-dark">Speichern</button>
              <button onClick={() => setEdit(null)} className="px-5 border border-edborder font-body text-sm hover:bg-offwhite">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
